import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Lookup } from '@shared-features/models/Lookup.model';
import { LookupService } from '@shared-features/services/lookups.service';
import { RoutesUtil } from '@shared-features/utils/routes.util';
import { BaseComponent } from '@shared/components/base-component/base.component';
import { ConfigConstant } from '@shared/constants/config.constant';
import { SearchCategory } from '@shared/enums/search-category.enum';
import { LookupType } from '@shared/enums/lookup-type.enum';
import { SectionStateStatus } from '@shared/enums/section-state-status.enum';
import { BehaviorSubject, combineLatest, fromEvent, timer } from 'rxjs';
import { debounceTime, filter, tap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchComponent extends BaseComponent {
  @ViewChild('scrollElement') scrollElementRef!: ElementRef<HTMLElement>;
  @ViewChild('loadMoreTrigger') loadMoreTriggerElementRef!: ElementRef<HTMLElement>;

  searchControl = new UntypedFormControl('');
  entities: Lookup[] = [];
  searchToogle = false;
  closeSearch = false;
  showSearchOptions = false;
  showSearchResult = false;
  loading = false;
  endOfResult = false;
  limit = 10;
  margin = 20;
  selectedCategory!: SearchCategory;
  offset = ConfigConstant.INIT_PAGE_OFFSET;

  private pagination$ = new BehaviorSubject<{ offset: number }>({ offset: 0 });
  SectionStateStatus = SectionStateStatus;
  SearchCategory = SearchCategory;

  constructor(
    private lookupService: LookupService,
    private router: Router,
    private changeDetector: ChangeDetectorRef
  ) {
    super();
    this.initializeSearchControl();
  }

  ngOnInit(): void {
    this.setupPaginationListener();
  }

  ngAfterViewInit(): void {
    this.handleScrollEvent();
  }

  private initializeSearchControl(): void {
    this.searchControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.showSearchOptions = Boolean(value);
      this.showSearchResult = false;
    });
  }

  private setupPaginationListener(): void {
    this.pagination$ = new BehaviorSubject({ offset: 0 });
    combineLatest([this.pagination$])
      .pipe(
        filter(() => !this.endOfResult),
        tap(() => (this.loading = true)),
        debounceTime(500),
        takeUntil(this.destroy$)
      )
      .subscribe((data) => {
        this.offset = data[0].offset | ConfigConstant.INIT_PAGE_OFFSET;
        this.getEntities(this.selectedCategory);
      });
  }

  searchByCategory(value: SearchCategory): void {
    this.resetSearchResults();
    this.selectedCategory = value;
    this.getEntities(value);
  }

  private getEntities(value: SearchCategory): void {
    const lookupTypeMap = {
      [SearchCategory.Driver]: LookupType.Drivers,
      [SearchCategory.Truck]: LookupType.Trucks,
      [SearchCategory.Customer]: LookupType.Customers,
    };

    const lookupType = lookupTypeMap[value];
    if (!lookupType) return;

    this.load(
      this.lookupService.getGlobalSearchLookup(
        lookupType,
        {
          offset: this.offset,
          limit: this.limit,
          searchTerm: this.searchControl.value,
        },
        false
      )
    ).subscribe((res) => {
      this.entities = res.length ? [...this.entities, ...res] : this.entities;
      this.endOfResult = res.length === 0;
      this.loading = false;
      this.showSearchResult = true;
      this.showSearchOptions = false;
    });
  }

  navigateToDetailsPage(id: number): void {
    const routeMap = {
      [SearchCategory.Driver]: RoutesUtil.FleetsDriversView.url({ id }),
      [SearchCategory.Truck]: RoutesUtil.FleetsTrucksView.url({ id }),
      [SearchCategory.Customer]: RoutesUtil.CustomersB2BView.url({ id }),
    };

    this.router.navigate([routeMap[this.selectedCategory]]);
    this.clear();
    this.searchToogle = false;
  }

  private handleScrollEvent(): void {
    const $scrollElement = this.scrollElementRef?.nativeElement;
    if (!$scrollElement) return;

    fromEvent($scrollElement, 'scroll')
      .pipe(
        debounceTime(500),
        filter(() => !this.loading && !this.endOfResult),
        filter(() => this.isLoadMoreTriggerVisible()),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.loadMore());
  }

  private isLoadMoreTriggerVisible(): boolean {
    const $scrollElement = this.scrollElementRef?.nativeElement;
    const $triggerElement = this.loadMoreTriggerElementRef?.nativeElement;

    if (!$scrollElement || !$triggerElement) return false;

    const { height } = $scrollElement.getBoundingClientRect();
    const { top } = $triggerElement.getBoundingClientRect();
    const scroll = $scrollElement.scrollTop;
    return top <= height + scroll + this.margin;
  }

  loadMore(): void {
    this.pagination$.next({ offset: this.offset + 1 });
  }

  clear(): void {
    this.resetSearchResults();
    this.searchControl.setValue(null);

    if (this.searchToogle) {
      this.closeSearch = !this.closeSearch;
      timer(500).subscribe(() => {
        this.closeSearch = !this.closeSearch;
        this.searchToogle = !this.searchToogle;
        this.changeDetector.markForCheck();
      });
    }
  }

  private resetSearchResults(): void {
    this.endOfResult = false;
    this.offset = ConfigConstant.INIT_PAGE_OFFSET;
    this.showSearchOptions = false;
    this.showSearchResult = false;
    this.entities = [];
  }

  resize$ = fromEvent(window, 'resize')
    .pipe(debounceTime(250), takeUntil(this.destroy$))
    .subscribe(() => this.resetSearchResults());
}
